<template>
    <div class="teeth-area">
        <div class="teeth-container">
            <div class="teeth-inner-container">
                <div class="teeth-objects" :style="isSafari ? 'aspect-ratio: unset;' : ''">
                    <div class="first-row">
                        <div v-for="(tooth, index) in firstRowTeethArray" class="teeth-and-margin"
                            :style="getsFixedHeight ? 'height: 100px;' : 'height: 100%;'"
                        >
                            <div :class="index === 0 || tooth === 21 ? 'margin-8px' : 'margin-6px'" :style="isSafari ? 'width: 10px' : ''" />
                            <div v-if="tooth === 21" class="margin-8px" :style="isSafari ? 'width: 0px' : ''" />
                            <div class="teeth-object">
                                <div 
                                    @click="selectTooth(tooth)" 
                                    :class="{ 'tooth-selected': isToothSelected(tooth) }"
                                >
                                    <div v-if="isLoading" class="tooth-loading">{{ tooth }}</div>
                                    <div v-else v-html="getToothSvgContent(tooth)"></div>
                                </div>
                            </div>
                            <div v-if="index === firstRowTeethArray.length -  1" class="margin-8px" />
                        </div>
                    </div>
                    <div class="second-row" :style="isSafari ? 'margin-right: 8px;' : 'margin-right: 13px;'">
                        <div v-for="(tooth, index) in secondRowTeethArray" class="teeth-and-margin"
                        :style="getsFixedHeight ? 'height: 100px;' : 'height: 100%;'"
                        >
                            <div :class="index === 0 ? 'margin-16px' : 'margin-6px'" :style="isSafari ? 'width: 10px' : ''"/>
                            <div v-if="tooth === 31" class="margin-10px" :style="isSafari ? 'display: none;' : ''"/>
                            <div class="teeth-object">
                                <div 
                                    @click="selectTooth(tooth)" 
                                    :class="{ 'tooth-selected': isToothSelected(tooth) }"
                                >
                                    <div v-if="isLoading" class="tooth-loading">{{ tooth }}</div>
                                    <div v-else v-html="getToothSvgContent(tooth)"></div>
                                </div>
                            </div>
                            <div v-if="index === secondRowTeethArray.length -  1" class="margin-22px" />
                        </div>
                    </div>
                </div>
                <div class="teeth-frame">
                    <img class="teeth-frame-img" src="@/assets/img/tooth_images/teeth_frame.svg" />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import {isAndroid, isIOS} from "@/utils/deviceCheck";

export default {
    name: 'TeethArea',
    props: {
        isSafari: Boolean,
        getsFixedHeight: Boolean,
        selectedTeethNumbers: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            firstRowTeethArray: [
                18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28,
            ], // 첫 번째 줄 치아번호
            secondRowTeethArray: [
                48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
            ], // 두 번째 줄 치아번호
            svgContents: {},
            isLoading: true
        }
    },


    async mounted() {
      //Vue에서는 데이터(state)를 변경하면, 변경사항이 자동으로 DOM에 업데이트 된다.
      //그러나, 데이터가 변경되자마자 바로 DOM에 반영되는 것은 아니다. vue는 DOM 업데이트를 비동기로 처리한다.
      //$nextTick을 이용해 데이터 변경 후 DOM까지 업데이트가 완료된 후 실행해야 더 안전할 것으로 보인다.
      
      // SVG 파일들을 미리 로드
      await this.loadSvgContents();
      
      this.$nextTick(() => {
        this.checkIsScrollable()
      })
    },

  methods: {
        selectTooth(tooth) {
            this.$emit('selectTooth', tooth);
        },

        isToothSelected(tooth) {
            return this.selectedTeethNumbers?.includes(tooth) || false;
        },

        async loadSvgContents() {
            const allTeeth = [...this.firstRowTeethArray, ...this.secondRowTeethArray];
            
            try {
                // 모든 SVG를 병렬로 로드
                const svgPromises = allTeeth.map(async (tooth) => {
                    try {
                        const svgModule = await import(`@/assets/img/tooth_images/tooth-${tooth}.svg`);
                        const response = await fetch(svgModule.default);
                        let svgContent = await response.text();
                        
                        // XML 선언 제거
                        svgContent = svgContent.replace(/<\?xml[^>]*\?>/, '');
                        
                        // alt 속성 및 불필요한 속성들 제거
                        svgContent = svgContent.replace(/\salt="[^"]*"/g, '');
                        svgContent = svgContent.replace(/\stitle="[^"]*"/g, '');
                        svgContent = svgContent.replace(/\sdesc="[^"]*"/g, '');
                        svgContent = svgContent.replace(/\sdata-name="[^"]*"/g, '');
                        
                        // SVG id 속성을 고유하게 만들기 (중복 방지)
                        svgContent = svgContent.replace(/id="([^"]+)"/g, `id="tooth-${tooth}-$1"`);
                        
                        return { tooth, svgContent };
                    } catch (error) {
                        console.error(`SVG 로드 실패: tooth-${tooth}.svg`, error);
                        return { tooth, svgContent: `<div>${this.$t('workorder_tooth_label')} ${tooth}</div>` };
                    }
                });
                
                // 모든 SVG 로드 완료 대기
                const svgResults = await Promise.all(svgPromises);
                
                // 결과를 객체로 변환
                const newSvgContents = {};
                svgResults.forEach(({ tooth, svgContent }) => {
                    newSvgContents[tooth] = svgContent;
                });
                
                // 전체 객체를 한 번에 할당하여 반응성 보장
                this.svgContents = newSvgContents;
                this.isLoading = false;
                
            } catch (error) {
                console.error('SVG 로딩 중 오류:', error);
                this.isLoading = false;
            }
        },

        getToothSvgContent(tooth) {
            return this.svgContents[tooth] || `<div>${this.$t('workorder_tooth_label')} ${tooth}</div>`;
        },

      //MARK: 치아영역 중앙으로 자동 스크롤
      checkIsScrollable() {
        /* teethArea가 null인 경우 종료. 만에 하나 있을지 모르는 런타임 에러 + 추후 발생할 수정사항 방어
          - 요소가 아직 DOM에 렌더링 되지 않은 경우
          - querySelector가 찾지 못하는 경우
          - display: none 인 경우
        */
        const teethArea = document.querySelector('.teeth-area')
        if (!teethArea) return

        /* scrollWidth와 clientWidth가 0이 되는 경우 종료. 만에 하나 있을지 모르는 런타임 에러 + 추후 발생할 수정사항 으로 인한 사이드이펙트 방어
          - 요소의 크기가 계산되기 전
          - CSS의 적용 시점 이전에 호출
          - 부모 요소가 숨겨진 상태
        */
        const scrollWidth = teethArea.scrollWidth
        const clientWidth = teethArea.clientWidth
        if (!scrollWidth || !clientWidth) return

        // iOS, AOS인 경우
        if (isIOS() || isAndroid()) {
          this.scrollToMiddle(teethArea, scrollWidth, clientWidth)
        } else {
          //데스크탑에서 브라우저를 작은 사이즈로 띄워서 보는 경우 == clientWidth가 560 보다 작은 경우
          if (clientWidth < 560) {
            this.scrollToMiddle(teethArea, scrollWidth, clientWidth)
          }
        }
      },

      scrollToMiddle(teethArea, scrollWidth, clientWidth) {
        const scrollTo = (scrollWidth - clientWidth) / 2
        teethArea.scrollTo({
          left: scrollTo,
          behavior: "smooth"
        })
      }
    }
}
</script>

<style scoped>
@media screen and (max-width:767px) {
    .teeth-area {
        justify-content: flex-start !important;
    }
}


/* 로딩 중 치아 스타일 */
.tooth-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: 12px;
    color: #ACB2B9;
    background-color: #F7F8F8;
    border-radius: 4px;
    border: 1px solid #E8EBED;
}

/* 치아 프레임이 클릭 이벤트를 방해하지 않도록 */
.teeth-frame {
    pointer-events: none !important;
}

.teeth-frame-img {
    pointer-events: none !important;
}

/* 치아 클릭 영역 확실히 설정 */
.teeth-object div {
    cursor: pointer !important;
    pointer-events: auto !important;
    position: relative;
    z-index: 10;
}

/* 선택된 치아 스타일 - 인라인 SVG 클래스 제어 */
.tooth-selected {
    cursor: pointer;
}

</style>
<style lang="scss">
    @import '@/assets/scss/teeth.scss';
    
    /* 인라인 SVG 크기 제어 */
    .teeth-object div {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }

     .teeth-object svg {
         width: auto;
         height: 100%;
         max-width: 100%;
         max-height: 100%;
         vertical-align: middle;
         flex-shrink: 0;
         pointer-events: none; /* SVG 내부 요소들이 클릭 이벤트를 방해하지 않도록 */
     }
    /* SVG 내부 클래스 제어 */
    .tooth-selected .b {
        fill: none !important; /* 투명 유지 */
    }

    .tooth-selected .c {
        fill: #278AEC !important; /* 치아 본체를 파란색으로 */
    }

    .tooth-selected .d {
        fill: #1f6bb8 !important; /* 치아 테두리를 더 진한 파란색으로 */
    }

    .tooth-selected .e {
        fill: white !important; /* 숫자 텍스트를 흰색으로 */
    }

</style>